import { FC, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { withPage } from '../../components/Page';
import { PrototypeBar } from '../../components/PrototypeBar';
import styles from './ContextualAi.module.scss';

interface ChatMessage {
  sender: 'user' | 'ai';
  text: string;
  sources?: string[];
}

const ContextualAi: FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      sender: 'user',
      text: 'What maintenance does this unit require?',
    },
    {
      sender: 'ai',
      text: 'AHU-003 requires quarterly filter inspection and an annual service.\n\nThe next planned task is the quarterly filter inspection, due 14 October 2026. The annual service includes fan belt condition, damper operation and controls checks.',
      sources: ['Maintenance schedule', 'O&M manual §6.2', 'Asset data'],
    },
    {
      sender: 'user',
      text: 'Where is the filter access panel?',
    },
    {
      sender: 'ai',
      text: 'The filter access panel is on the south side of the unit, facing the plant-room entrance. See drawing M-PL01-200 for the access clearance.',
    },
  ]);
  const [inputVal, setInputVal] = useState('');

  const handleSend = (e: FormEvent) => {
    e.preventDefault();
    if (!inputVal.trim()) return;

    const newMsg: ChatMessage = { sender: 'user', text: inputVal.trim() };
    const replyMsg: ChatMessage = {
      sender: 'ai',
      text: `Based on AHU-003 and Plant Room 01 documentation, "${inputVal.trim()}" refers to standard operational procedures in the Trane CCU-240 manual. Please verify local isolation before proceeding.`,
      sources: ['O&M manual §4.1', 'Asset data'],
    };

    setMessages((prev) => [...prev, newMsg, replyMsg]);
    setInputVal('');
  };

  return (
    <main className={styles.container} tabIndex={-1}>
      <PrototypeBar />

      <div className={styles.mobileStage}>
        <div className={styles.mobileCopy}>
          <div className={styles.eyebrow}>Mobile / contextual Ask AI</div>
          <h2>Ask a question without explaining the context.</h2>
          <p>
            Pulse carries the asset, location and relevant source information into the conversation, then points the engineer back to the source records.
          </p>
          <div className={styles.flow}>
            <b>AHU-003 context</b> → <b>Ask a question</b> → <b>Source-backed answer</b>
          </div>
          <div style={{ marginTop: '2.4rem' }}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => navigate('/asset-data/ahu-003')}
            >
              View desktop asset record (Screen 01)
            </button>
          </div>
        </div>

        <div className={styles.phone}>
          <div className={styles.phoneTop}>
            <span>9:41</span>
            <span>● ● ●</span>
          </div>
          <div className={styles.phoneContent}>
            <div className={styles.phoneHeaderRow}>
              <button
                type="button"
                className={styles.backBtn}
                onClick={() => navigate('/mobile-asset')}
              >
                ‹
              </button>
              <b style={{ fontSize: '1.6rem', color: 'var(--color-text-primary)' }}>Ask AI</b>
            </div>

            <div className={styles.aiContext}>
              <b>Context: AHU-003</b>
              <br />
              Air Handling Unit · Plant Room 01
              <br />
              <span>6 documents · 2 drawings · 14 asset records</span>
            </div>

            <div className={styles.chat}>
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`${styles.msg} ${m.sender === 'user' ? styles.user : styles.ai}`}
                >
                  {m.sender === 'ai' ? (
                    <div>
                      {m.text.split('\n\n').map((paragraph, pIdx) => (
                        <p key={pIdx} style={{ margin: pIdx === 0 ? '0 0 8px' : '0' }}>
                          {pIdx === 0 && paragraph.includes('requires') ? (
                            <strong>{paragraph}</strong>
                          ) : (
                            paragraph
                          )}
                        </p>
                      ))}
                      {m.sources && (
                        <div className={styles.sources}>
                          {m.sources.map((src, sIdx) => (
                            <span key={sIdx}>{src}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    m.text
                  )}
                </div>
              ))}
            </div>
          </div>

          <form className={styles.inputForm} onSubmit={handleSend}>
            <input
              type="text"
              className={styles.inputField}
              placeholder="Ask a question about AHU-003..."
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              aria-label="Ask a question about AHU-003"
            />
            <button type="submit" className={styles.sendBtn} aria-label="Send message">
              ↑
            </button>
          </form>

          <div className={styles.mobileNav}>
            <span>⌂<br />Home</span>
            <span>⌕<br />Search</span>
            <b style={{ color: '#007f85' }}>✦<br />Ask AI</b>
            <span>▤<br />Documents</span>
            <span>☰<br />More</span>
          </div>
        </div>
      </div>
    </main>
  );
};

export default withPage(ContextualAi)({
  meta: {
    title: 'Contextual Ask AI — AHU-003 · Pulse',
    description: 'Context-aware AI assistant for AHU-003.',
  },
  breadcrumbs: [
    { label: 'Pulse', href: '/' },
    { label: 'Commerzbank', href: '/' },
    { label: 'Contextual AI', href: '/contextual-ai' },
  ],
});
